/**
 * ConceptPanel — pannello "Tutte le relazioni" del fuoco: intestazione,
 * riassunto, "Ascolta", lista delle proposizioni (frase meccanica + puntino
 * colore famiglia + riga meta), esterni cliccabili.
 *
 * Riusato da web (docked ≥1024 / sheet <1024) e desktop (sempre docked,
 * `readOnly` gestito dal chiamante tramite `onGoToManual` opzionale).
 */
import type { ChapterGraph, MapLocale, Proposition } from '@arianna/reader-core'
import { FAMILY_LABEL } from './familyLabel.js'

export interface ConceptPanelProps {
  graph: ChapterGraph
  nodeId: string
  propositions: Proposition[]
  locale: MapLocale
  docked: boolean
  onClose(): void
  onGoToManual?(blockId: string): void
  onSpeak?(text: string): void
  onGoToExternal(target: { chapterId: string; nodeId: string }): void
}

const META_LABEL: Record<
  MapLocale,
  { restingHidden: string; listen: string; close: string; goToManual: string }
> = {
  it: {
    restingHidden: 'nascosta a riposo',
    listen: 'Ascolta',
    close: 'Chiudi',
    goToManual: 'Vai al manuale →',
  },
  en: {
    restingHidden: 'hidden at rest',
    listen: 'Listen',
    close: 'Close',
    goToManual: 'Go to manual →',
  },
}

export function ConceptPanel({
  graph,
  nodeId,
  propositions,
  locale,
  docked,
  onClose,
  onGoToManual,
  onSpeak,
  onGoToExternal,
}: ConceptPanelProps) {
  const t = META_LABEL[locale]
  const node = graph.nodes.find((n) => n.nodeId === nodeId)
  if (node == null) return null

  const summaryText = node.summary.length > 0 ? node.summary : node.label
  const fullText = [summaryText, ...propositions.map((p) => p.text)].join('. ')

  return (
    <div
      className={`cm-panel${docked ? ' cm-panel--docked' : ' cm-panel--sheet'}`}
      role="dialog"
      aria-label={`Relazioni di ${node.label}`}
      data-testid="concept-panel"
    >
      <div className="cm-panel__header">
        <div className="cm-panel__title">{node.label}</div>
        <button
          type="button"
          className="cm-panel__close"
          onClick={onClose}
          aria-label={t.close}
          data-testid="concept-panel-close"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>

      {summaryText.length > 0 && <p className="cm-panel__summary">{summaryText}</p>}

      {onSpeak != null && (
        <button
          type="button"
          className="cm-panel__listen"
          onClick={() => onSpeak(fullText)}
          data-testid="concept-panel-listen"
        >
          {t.listen}
        </button>
      )}

      <ul className="cm-panel__list">
        {propositions.map((p) => {
          // family === null -> relationType sconosciuto (v. buildPropositions):
          // nessuna etichetta di famiglia, mostra il tipo grezzo dell'arco.
          const familyLabel =
            p.family != null
              ? FAMILY_LABEL[locale][p.family]
              : (graph.edges.find((e) => e.edgeId === p.edgeId)?.relationType ?? '')
          return (
            <li key={p.edgeId} className="cm-panel__item">
              <span className={`cm-panel__dot cm-panel__dot--${p.family}`} aria-hidden="true" />
              <div className="cm-panel__item-body">
                {p.external ? (
                  <button
                    type="button"
                    className="cm-panel__external"
                    data-testid="concept-panel-external"
                    onClick={() => {
                      const externalNode = graph.externalNodes.find(
                        (e) => e.nodeId === p.otherNodeId,
                      )
                      if (externalNode == null) return
                      onGoToExternal({
                        chapterId: externalNode.chapterId,
                        nodeId: externalNode.nodeId,
                      })
                    }}
                  >
                    {p.text}
                  </button>
                ) : (
                  <span className="cm-panel__text">{p.text}</span>
                )}
                <span className="cm-panel__meta">
                  {familyLabel}
                  {!p.resting ? ` · ${t.restingHidden}` : ''}
                </span>
              </div>
            </li>
          )
        })}
      </ul>

      {onGoToManual != null && node.sourceBlockIds.length > 0 && (
        <button
          type="button"
          className="cm-panel__manual"
          data-testid="concept-panel-go-to-manual"
          onClick={() => onGoToManual(node.sourceBlockIds[0]!)}
        >
          {t.goToManual}
        </button>
      )}
    </div>
  )
}
